import { isValidMediaUrl, MAX_IMAGE_BYTES, MAX_VIDEO_BYTES, type Media } from '@survey/core';
import { Image as ImageIcon, ImagePlus, Link2, Trash2, Upload, Video } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, ApiError, mediaUrl } from '../../api/client';
import { useI18n } from '../../i18n';
import { QuestionMedia } from '../QuestionMedia';
import { Field, Spinner, Switch, useToast } from '../ui';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime';

/** Uploads a file after a client-side size check; returns the stored URL or null. Tracks progress and the last error. */
function useUpload() {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ name: string; fraction: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fail = (message: string) => {
    setError(message);
    toast(message, 'error');
    return null;
  };
  const upload = async (file: File, kind: 'image' | 'video') => {
    setError(null);
    const max = kind === 'image' ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES;
    if (file.size > max) return fail(t('media.too_large_file', { name: file.name, size: Math.ceil(file.size / (1 << 20)), max: max >> 20 }));
    setBusy(true);
    setProgress({ name: file.name, fraction: 0 });
    try {
      const res = await api.uploadMedia(file, (fraction) => setProgress({ name: file.name, fraction }));
      if (res.type !== kind) return fail(t('media.wrong_kind'));
      return res.url;
    } catch (e) {
      return fail(e instanceof ApiError ? e.fields.file || e.message : t('media.upload_failed'));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };
  return { busy, upload, progress, error };
}

/** What an upload is doing: a progress bar while it runs, or why it failed. */
function UploadStatus({ progress, error }: { progress: { name: string; fraction: number } | null; error: string | null }) {
  const { t } = useI18n();
  if (progress) {
    const pct = Math.round(progress.fraction * 100);
    return (
      <div className="upload-progress" role="status" aria-live="polite">
        <div className="row gap-8 small">
          <span className="grow ellipsis">{t('media.uploading_file', { name: progress.name })}</span>
          <strong>{pct}%</strong>
        </div>
        <div className="upload-bar" aria-hidden>
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  return error ? (
    <p className="upload-error small" role="alert">
      {error}
    </p>
  ) : null;
}

/** Builder controls for a question's image or video. */
export function MediaEditor({ media, onChange }: { media?: Media; onChange: (m: Media | undefined) => void }) {
  const { t } = useI18n();
  const { busy, upload, progress, error } = useUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<'image' | 'video'>(media?.type ?? 'image');
  const [link, setLink] = useState('');
  const linkOk = isValidMediaUrl(link.trim());

  const pick = (k: 'image' | 'video') => {
    setKind(k);
    requestAnimationFrame(() => fileRef.current?.click());
  };

  if (media) {
    return (
      <div className="media-editor">
        <QuestionMedia media={media} />
        <Field label={media.type === 'image' ? t('media.alt') : t('media.caption')} htmlFor="q-media-alt">
          <input
            id="q-media-alt"
            className="input"
            maxLength={300}
            value={media.alt ?? ''}
            placeholder={media.type === 'image' ? t('media.alt_placeholder') : t('media.caption_placeholder')}
            onChange={(e) => onChange({ ...media, alt: e.target.value || undefined })}
          />
        </Field>
        {media.type === 'video' && (
          <div className="row gap-16 wrap">
            <Switch checked={!!media.autoplay} onChange={(autoplay) => onChange({ ...media, autoplay: autoplay || undefined })} label={t('media.autoplay')} />
            <Switch checked={!!media.muted} onChange={(muted) => onChange({ ...media, muted: muted || undefined })} label={t('media.muted')} />
          </div>
        )}
        {media.type === 'video' && media.autoplay && !media.muted && <p className="hint">{t('media.autoplay_hint')}</p>}
        <div>
          <button type="button" className="btn sm danger" onClick={() => onChange(undefined)}>
            <Trash2 /> {media.type === 'image' ? t('media.remove_image') : t('media.remove_video')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="media-editor">
      <div className="row gap-8 wrap">
        <button type="button" className="btn sm" disabled={busy} onClick={() => pick('image')}>
          <ImageIcon /> {t('media.upload_image')}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={() => pick('video')}>
          <Video /> {t('media.upload_video')}
        </button>
        {busy && !progress && <Spinner label={t('media.uploading')} />}
      </div>
      <input
        ref={fileRef}
        type="file"
        hidden
        accept={kind === 'image' ? IMAGE_ACCEPT : VIDEO_ACCEPT}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          const url = await upload(file, kind);
          if (url) onChange({ type: kind, url });
        }}
      />
      <div className="row gap-8">
        <Link2 className="muted" aria-hidden />
        <input
          className="input"
          value={link}
          aria-label={t('media.link')}
          placeholder={t('media.link_placeholder')}
          onChange={(e) => setLink(e.target.value)}
        />
        <button
          type="button"
          className="btn sm"
          disabled={!linkOk}
          onClick={() => {
            const url = link.trim();
            setLink('');
            onChange({ type: /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url) ? 'image' : 'video', url });
          }}
        >
          {t('media.add_link')}
        </button>
      </div>
      <UploadStatus progress={progress} error={error} />
      <p className="hint">{t('media.hint', { image: MAX_IMAGE_BYTES >> 20, video: MAX_VIDEO_BYTES >> 20 })}</p>
    </div>
  );
}

/** Small button that attaches, shows and removes an option's picture. */
export function OptionImage({ url, label, onChange }: { url?: string; label: string; onChange: (url: string | undefined) => void }) {
  const { t } = useI18n();
  const { busy, upload } = useUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  if (url) {
    return (
      <button type="button" className="btn ghost sm icon" title={t('media.remove_option_image')} aria-label={t('media.remove_option_image_n', { label })} onClick={() => onChange(undefined)}>
        <img className="option-thumb" src={mediaUrl(url)} alt="" />
      </button>
    );
  }
  return (
    <>
      <button type="button" className="btn ghost sm icon" disabled={busy} title={t('media.add_option_image')} aria-label={t('media.add_option_image_n', { label })} onClick={() => fileRef.current?.click()}>
        {busy ? <Upload /> : <ImagePlus />}
      </button>
      <input
        ref={fileRef}
        type="file"
        hidden
        accept={IMAGE_ACCEPT}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          const u = await upload(file, 'image');
          if (u) onChange(u);
        }}
      />
    </>
  );
}
